import { useNavigation } from '@react-navigation/native'
import React, { useCallback, useEffect } from 'react'
import { BackHandler } from 'react-native'
import { useSnapshot } from 'valtio'

import { useSessionCart } from '../../context/cart'
import { useConfig } from '../../context/config'
import { useCartProduct } from '../../hooks/use-cart-product'
import { state as paymentState } from '../../hooks/use-payment-process'
import { unlockSlot } from '../../lib/api/schedule/slot-lock'
import { HomeStackScreenProps } from '../../navigation/MainTabs/Home'
import SignInHeaderLeft from '../SignInHeaderLeft/SignInHeaderLeft'

interface Props {
  shouldConsiderClinicianCalendarRoute?: boolean
  shouldConsiderSelectProfileRoute?: boolean
  shouldUnlockSlot?: boolean
}

// This component is used as the core back button for the purchase flow
export const HomeHeaderLeft: React.FC<Props> = ({
  shouldConsiderClinicianCalendarRoute,
  shouldConsiderSelectProfileRoute,
  shouldUnlockSlot,
}) => {
  const { config } = useConfig()
  const navigation =
    useNavigation<HomeStackScreenProps<'Checkout' | 'SelectProfile' | 'ClinicianCalendar'>['navigation']>()
  const { product, clinician, schedule } = useCartProduct()
  const { sessionKey } = useSessionCart()
  const paymentSnapshot = useSnapshot(paymentState)

  const unlockClinicianSlot = useCallback(
    (clinicianId?: number) => {
      if (!clinicianId) return

      return unlockSlot(clinicianId, sessionKey)
    },
    [sessionKey]
  )

  useEffect(() => {
    const backAction = () => {
      if (schedule) {
        unlockClinicianSlot(clinician?.id)
      }
      return true
    }
    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction)

    return () => backHandler.remove()
  }, [clinician?.id, schedule, unlockClinicianSlot])

  const handleBackNavigation = useCallback(() => {
    if (navigation.canGoBack()) {
      if (shouldUnlockSlot && clinician?.id && schedule) unlockClinicianSlot(clinician.id)
      return navigation.goBack()
    }

    if (!product) {
      return navigation.reset({
        index: 0,
        routes: [{ name: 'HowCanWeHelp' }],
      })
    }

    if (shouldConsiderSelectProfileRoute && config.canPatientAddDependents && product) {
      return navigation.reset({
        index: 0,
        routes: [{ name: 'SelectProfile' }],
      })
    }

    if (shouldConsiderClinicianCalendarRoute && clinician?.id && schedule) {
      unlockClinicianSlot(clinician.id)
      return navigation.reset({
        index: 0,
        routes: [
          {
            name: 'ClinicianCalendar',
            params: { productSlug: product.slug, clinicianId: clinician?.id, timeout: false },
          },
        ],
      })
    }

    return navigation.reset({
      index: 0,
      routes: [
        {
          name: 'Product',
          params: { slug: product.slug },
        },
      ],
    })
  }, [product, clinician?.id, schedule, navigation, config.canPatientAddDependents, unlockClinicianSlot])

  return (
    <SignInHeaderLeft disabled={paymentSnapshot.isPaymentOngoing} canGoBack={true} onPress={handleBackNavigation} />
  )
}

export default HomeHeaderLeft
